import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Icon404File, Modal, SelectField,
} from 'gatorcomponents';
import * as Styles from './ModalInitialQuestions.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import FieldItem from './Forms/FieldItem';
import HGImageStudio from './HGImageStudio';
import useLocale from '@/hooks/useLocale';
import { useResize } from '@/hooks/useResize';
import { useSelector } from 'react-redux';

const ModalInitialQuestions = ({ isProPlan, handleSubmit, setFormHasAnswered }) => {
  const { showLoadingError } = useSelector(state => state.studio);
  const { studio: studioLocale } = useLocale();
  const [selectedQuestion1, setSelectedQuestion1] = useState([]);
  const [selectedQuestion2, setSelectedQuestion2] = useState([]);
  const [selectedQuestion3, setSelectedQuestion3] = useState([]);
  const [shouldDisableButton, setShouldDisableButton] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const question1 = studioLocale.modalInitialQuestions.question1 || [];
  const question3 = studioLocale.modalInitialQuestions.question3 || [];
  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < 960;

  const mountOptions = (options) => {
    const optionsList = [];

    Object.entries(options).forEach(([key, value]) => {
      optionsList.push({
        value: key,
        label: value,
      });
    });

    optionsList.sort((a, b) => a.label.localeCompare(b.label));

    const index = optionsList.findIndex(option => option.value === 'others');
    if (index !== -1) {
      const [itemOthers] = optionsList.splice(index, 1);
      optionsList.push(itemOthers);
    }

    return optionsList;
  };

  const handleQuestion1 = (name) => {
    setSelectedQuestion1((prevSelectedQuestion) => {
      if (prevSelectedQuestion.includes(name)) {
        return prevSelectedQuestion.filter(item => item !== name);
      }
      return [...prevSelectedQuestion, name];
    });
  };

  const handleQuestion2 = (option) => {
    setSelectedQuestion2([{ label: option.label, value: option.value }]);
  };

  const handleQuestion3 = (name) => {
    setSelectedQuestion3([name]);
  };

  const isActive = (name, question) => {
    switch (question) {
      case 'question1':
        return selectedQuestion1.includes(name);
      case 'question3':
        return selectedQuestion3.includes(name);
      default:
        return null;
    }
  };

  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  useEffect(() => {
    setShouldDisableButton(selectedQuestion2.length === 0 || selectedQuestion3.length === 0);
  }, [selectedQuestion2, selectedQuestion3]);

  const handleFormError = useCallback(() => {
    showLoadingError && setCurrentStep(3);
  }, [showLoadingError]);

  useEffect(handleFormError, [handleFormError]);

  const handleSendForm = () => {
    setLoading(true);

    const dataToSend = {
      answer1: selectedQuestion1,
      answer2: selectedQuestion2.map(option => option.label),
      answer3: selectedQuestion3,
    };

    handleSubmit && handleSubmit(dataToSend);
    setFormHasAnswered(true);
  };

  const handleCloseModal = () => {
    const dataToSend = {
      answer1: selectedQuestion1.length > 0 ? selectedQuestion1 : ['não respondeu'],
      answer2: selectedQuestion2.length > 0 ? selectedQuestion2.map(option => option.label) : ['não respondeu'],
      answer3: selectedQuestion3.length > 0 ? selectedQuestion3 : ['não respondeu'],
    };

    setFormHasAnswered(true);
    handleSubmit && handleSubmit(dataToSend);
  };

  return (
    <Modal
      maxHeight="100vh"
      maxWidth={mobile ? 600 : 960}
      noHeader
      noContentPadding
      onClose={handleCloseModal}
      testId="Modal"
    >
      <Styles.ModalContent>
        {currentStep !== 3 && (
        <Styles.WrapperForm>
          <Styles.Title isProPlan={isProPlan}>
            {isProPlan ? studioLocale.modalInitialQuestions.titlePro : studioLocale.modalInitialQuestions.title }
          </Styles.Title>

          {currentStep === 1 && (
            <>
              <Styles.Step step={`step-${currentStep}`}>
                <Styles.Description>{studioLocale.modalInitialQuestions.labelQuestion1}</Styles.Description>
                <Styles.WrapperList>
                  {Object.entries(question1).map(([key, value]) => (
                    <FieldItem
                      data-testid={key}
                      key={key}
                      name={value}
                      onClick={() => handleQuestion1(value)}
                      active={isActive(value, 'question1')}
                    />
                  ))}
                </Styles.WrapperList>

                <Styles.ModalActions>
                  <Button
                    disabled={selectedQuestion1.length === 0}
                    label={studioLocale.modalInitialQuestions.buttonContinue}
                    onClick={() => handleNextStep()}
                    size="large"
                    testId="buttonNextStep"
                    title={studioLocale.modalInitialQuestions.buttonContinue}
                    isProPlan="button"
                    variant="primary"
                    version="v2"
                    width={mobile ? 'full' : 'fit-content'}
                  />
                </Styles.ModalActions>
              </Styles.Step>
            </>
          )}

          {currentStep === 2 && (
            <Styles.Step step={`step-${currentStep}`}>

              <Styles.Row variant="column">
                <Styles.Description>{studioLocale.modalInitialQuestions.labelQuestion2}</Styles.Description>
                <SelectField
                  id="Select"
                  label="Title"
                  onClickSelect={handleQuestion2}
                  options={mountOptions(studioLocale.bussinessCategories)}
                  optionsPosition="bottom"
                  placeholder="Selecione uma categoria"
                  value={selectedQuestion2.length > 0 ? selectedQuestion2[0].label : ''}
                  size="md"
                  withScroll
                />
              </Styles.Row>

              <Styles.Row variant="column">
                <Styles.Description>{studioLocale.modalInitialQuestions.labelQuestion3}</Styles.Description>
                <Styles.Row>
                  {Object.entries(question3).map(([key, value]) => (
                    <FieldItem
                      key={key}
                      name={value}
                      onClick={() => handleQuestion3(value)}
                      active={isActive(value, 'question3')}
                      textAlign="center"
                    />
                  ))}
                </Styles.Row>
              </Styles.Row>

              <Styles.ModalActions>
                <Button
                  label={studioLocale.modalInitialQuestions.buttonBack}
                  onClick={() => handlePrevStep()}
                  size="large"
                  testId="button"
                  title={studioLocale.modalInitialQuestions.buttonBack}
                  isProPlan="button"
                  variant="tertiary"
                  version="v2"
                  width={mobile ? 'full' : 'fit-content'}
                />
                <Button
                  disabled={shouldDisableButton || loading}
                  label={studioLocale.modalInitialQuestions.buttonContinue}
                  onClick={handleSendForm}
                  size="large"
                  testId="buttonSubmit"
                  title={studioLocale.modalInitialQuestions.buttonContinue}
                  isProPlan="button"
                  variant="primary"
                  version="v2"
                  width={mobile ? 'full' : 'fit-content'}
                />
              </Styles.ModalActions>
            </Styles.Step>
          )}
        </Styles.WrapperForm>
        )}

        {!mobile && currentStep !== 3 && (
          <Styles.WrapperImage>
            <HGImageStudio step={currentStep} isProPlan={isProPlan} />
          </Styles.WrapperImage>
        )}

        {currentStep === 3 && (
        <Styles.Step step={`step-${currentStep}`}>
          <Icon404File
            color="lowLight"
            secohndaryColor="highPure"
            size="xl"
            testId="myIconTestId"
          />
          <Styles.TextError>
            {studioLocale.modalInitialQuestions.errorDescription}
          </Styles.TextError>

          <Styles.WrapperButtonError>
            <Button
              label={studioLocale.modalInitialQuestions.buttonError}
              onClick={handleCloseModal}
              size="large"
              testId="button"
              title={studioLocale.modalInitialQuestions.buttonError}
              type="button"
              variant="primary"
              version="v2"
              width="fit-content"
            />
          </Styles.WrapperButtonError>
        </Styles.Step>
        )}
      </Styles.ModalContent>
    </Modal>
  );
};

export default withErrorBoundary(ModalInitialQuestions, 'ModalInitialQuestions');
